import React, { useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { Zap, X } from 'lucide-react';
import { AuthTabs } from './auth/AuthTabs';
import { useAuth } from '../contexts/AuthContext';
import { Navbar } from './Navbar';

export function PublicHeader() {
  const location = useLocation();
  const { user } = useAuth();
  const isLandingPage = location.pathname === '/';
  const isAdminPage = location.pathname.startsWith('/admin');
  const [showSignInModal, setShowSignInModal] = useState(false);
  const [showSignUpModal, setShowSignUpModal] = useState(false);

  // If on admin pages, don't show any navigation
  if (isAdminPage) {
    return null;
  }

  // If user is authenticated and not on admin pages, show the main navigation
  if (user) {
    return <Navbar isCollapsed={false} onCollapse={() => {}} />;
  }

  // Otherwise show the public header
  return (
    <>
      <header className="bg-bw-surface shadow-sm">
        {/* Brand color strip */}
        <div className="h-1 bg-bw-sidebar"></div>
        
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between h-16 items-center">
            {/* Logo */}
            <Link to="/" className="flex items-center">
              <Zap className="h-8 w-8 text-bw-accent" />
              <span className="ml-2 text-2xl font-bold text-bw-primary">BidWin</span>
            </Link>

            {/* Navigation Links */}
            <nav className="hidden md:flex items-center space-x-8">
              <Link 
                to="/blog" 
                className={`text-sm font-medium ${
                  location.pathname === '/blog'
                    ? 'text-bw-primary'
                    : 'text-bw-muted hover:text-bw-ink'
                }`}
              >
                The Winning Bid
              </Link>
              <Link 
                to="/docs" 
                className={`text-sm font-medium ${
                  location.pathname === '/docs'
                    ? 'text-bw-primary'
                    : 'text-bw-muted hover:text-bw-ink'
                }`}
              >
                Documentation
              </Link>
              <Link 
                to="/faq" 
                className={`text-sm font-medium ${
                  location.pathname === '/faq'
                    ? 'text-bw-primary'
                    : 'text-bw-muted hover:text-bw-ink'
                }`}
              >
                FAQ
              </Link>
            </nav>

            {/* Auth Buttons */}
            {!isLandingPage && (
              <div className="flex items-center space-x-4">
                <button
                  onClick={() => setShowSignInModal(true)}
                  className="text-sm font-medium text-bw-muted hover:text-bw-ink"
                >
                  Sign In
                </button>
                <button
                  onClick={() => setShowSignUpModal(true)}
                  className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-servet-red hover:bg-servet-red/90"
                >
                  Get Started
                </button>
              </div>
            )}
          </div>
        </div>
      </header>

      {/* Sign In Modal */}
      {showSignInModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4">
          <div className="bg-bw-surface rounded-lg shadow-xl max-w-md w-full relative">
            <button
              onClick={() => setShowSignInModal(false)}
              className="absolute top-4 right-4 text-bw-muted hover:text-bw-muted z-10"
            >
              <span className="sr-only">Close</span>
              <X className="h-6 w-6" />
            </button>
            <div className="p-8">
              <AuthTabs 
                initialTab="signin"
                onSwitchToSignUp={() => {
                  setShowSignInModal(false);
                  setShowSignUpModal(true);
                }}
              />
            </div>
          </div>
        </div>
      )}

      {/* Sign Up Modal */}
      {showSignUpModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4">
          <div className="bg-bw-surface rounded-lg shadow-xl max-w-md w-full relative">
            <button
              onClick={() => setShowSignUpModal(false)}
              className="absolute top-4 right-4 text-bw-muted hover:text-bw-muted z-10"
            >
              <span className="sr-only">Close</span>
              <X className="h-6 w-6" />
            </button>
            <div className="p-8">
              <AuthTabs 
                initialTab="signup"
                onSwitchToSignIn={() => {
                  setShowSignUpModal(false);
                  setShowSignInModal(true);
                }}
              />
            </div>
          </div>
        </div>
      )}
    </>
  );
}